/**
 * Public Credential Verification page.
 *
 * Routes:
 *   /verify/credential/:token           (via share link)
 *   /verify/credential-id/:credential_id (direct check)
 *
 * Unauthenticated. Renders a lean, PII-safe verification card that a
 * regulator or receiving institution can rely upon.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { CheckCircle2, XCircle, ShieldCheck, Fingerprint } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function VerifyCredentialPage() {
  const params = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = params.token
        ? `${BACKEND_URL}/api/verify/credential/${params.token}`
        : `${BACKEND_URL}/api/verify/credential-id/${params.credential_id}`;
      const { data } = await axios.get(url);
      setResult(data);
    } catch (e) {
      setResult({ is_valid: false, reason: e?.response?.data?.detail || "Verification failed" });
    } finally {
      setLoading(false);
    }
  }, [params.token, params.credential_id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 text-sm text-slate-500">Verifying credential…</div>;

  const valid = result?.is_valid;

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-6" data-testid="verify-credential">
      <article className="max-w-lg w-full mt-16 rounded-2xl bg-white border border-slate-100 shadow-lg overflow-hidden">
        <header
          className="p-5 text-white"
          style={{ background: `linear-gradient(135deg, ${NAVY}, #14304F)` }}
        >
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            Altara · Trust Credential Verification
          </div>
          <div className="flex items-center gap-2 mt-2">
            {valid
              ? <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              : <XCircle className="w-6 h-6 text-red-300" />}
            <h1 data-testid="verify-headline" className="font-display text-2xl font-semibold">
              {valid ? "Credential Verified" : "Credential Not Valid"}
            </h1>
          </div>
          {result?.reason && (
            <div className="text-[13px] text-slate-200 mt-1">{result.reason}</div>
          )}
        </header>

        {valid && (
          <div className="p-5 space-y-4">
            <Row label="Credential"  value={result.display_name} strong />
            <Row label="Issued by"   value={result.issuer_name} />
            <Row label="Level"       value={result.verification_level.replace(/_/g, " ")} />
            <Row label="Category"    value={result.category} />
            <Row label="Issued"      value={format(new Date(result.issued_at), "d MMM yyyy")} />
            <Row label="Expires"     value={result.expires_at ? format(new Date(result.expires_at), "d MMM yyyy") : "No expiry"} />
            <Row label="Evidence"    value={`${result.evidence_count} hash-chained record(s)`} />
            {result.trust_score != null && (
              <Row label="Trust score" value={`${result.trust_score} / 100`} />
            )}
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="text-[12px] text-emerald-900 leading-relaxed">
                {result.verification_context}
              </div>
            </div>
            <div className="text-[10px] text-slate-400 font-mono pt-2 border-t border-slate-100 flex items-center gap-1">
              <Fingerprint className="w-3 h-3" /> {result.credential_id}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-slate-50 pb-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono w-24 shrink-0">{label}</div>
      <div className={`text-[13px] ${strong ? "font-semibold" : ""}`} style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
